import React, { useEffect, useMemo, useState , useContext} from "react";
import axios from "axios";
import { Card, Row, Col, Table, Form, Button, Spinner } from "react-bootstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LabelList } from "recharts";
import * as XLSX from "xlsx";
import {
  BarChart3,
  PieChart as PieChartIcon,
  Download,
  IndianRupee,
  ShoppingCart,
  Crown,
  Filter, FileSpreadsheet
} from "lucide-react";
import MyContext from "../../MyContext";
/**
 * Usage:
 * <TopBestSellingReport baseUrl={baseUrl} config={config} userID={userID} />
 *
 * Notes:
 * - Uses Bootstrap classes for sleek spacing.
 * - Tight charts with ResponsiveContainer to avoid empty feel.
 * - Small subheading “Top selling items”.
 * - Platform select (POS/WhatsApp) wired to API.
 */

const COLORS = ["#6366F1", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4", "#8B5CF6", "#10B981", "#F97316"];

const currency = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const skeletonRows = new Array(4).fill(0).map((_, i) => ({ productId: i }));

const TopBestSellingReport = ({ userID }) => {
      const { baseUrl } = useContext(MyContext);
      const { config } = useContext(MyContext);
  const [platform, setPlatform] = useState("pos");
  const [interval, setInterval] = useState("30");
  const [loading, setLoading] = useState(false);

  const [topOrders, setTopOrders] = useState([]);
  const [topOrdersAnalytics, setTopOrdersAnalytics] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const body = { userid: userID, interval, platform };
      const res = await axios.post(`${baseUrl}/api/Order/v1.0/getBestSellingProducts`, body, config);

      if (res?.data?.responseCode === "00") {
        const items = res.data.response.map((d) => ({
          ...d,
          orderCount: Number(d.orderCount) || 0,
          totalPrice: Number(d.totalPrice) || 0,
        }));
        setTopOrders(items.slice(0, 4));
        setTopOrdersAnalytics(items);
      } else {
        setTopOrders([]);
        setTopOrdersAnalytics([]);
      }
    } catch (err) {
      console.error("API error:", err);
      setTopOrders([]);
      setTopOrdersAnalytics([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [platform, interval, userID]);

  const totals = useMemo(() => {
    const revenue = topOrdersAnalytics.reduce((s, i) => s + (i.totalPrice || 0), 0);
    const orders = topOrdersAnalytics.reduce((s, i) => s + (i.orderCount || 0), 0);
    const top = [...topOrdersAnalytics].sort((a, b) => b.orderCount - a.orderCount)[0];
    return { revenue, orders, top };
  }, [topOrdersAnalytics]);

  const exportExcel = () => {
    const rows = topOrdersAnalytics.map((d) => ({
      Product: d.productName,
      Orders: d.orderCount,
      Revenue: d.totalPrice,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TopBestSelling");
    XLSX.writeFile(wb, "TopBestSellingReport.xlsx");
  };

  const noData = !loading && topOrdersAnalytics.length === 0;

  return (
    <div className="m-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
       
          <div className="text-center mb-4 CreateCampaign">
           Reports And Analytics
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Filter size={18} className="text-muted" />
          <Form.Select
            size="sm"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            style={{ width: 150 }}
          >
            <option value="pos">POS</option>
            <option value="whatsapp">WhatsApp</option>
          </Form.Select>
          <Form.Select
            size="sm"
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
            style={{ width: 120 }}
          >
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
          </Form.Select>
          <Button variant="primary" size="sm" onClick={exportExcel} className="d-flex align-items-center gap-2">
            <Download size={16} /> Export
          </Button>
        </div>
      </div>

      {/* Tiny subheading */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <BarChart3 size={20} className="text-primary" />
          <h5 className="mb-0 fw-bold text-dark">Top Best-Selling Orders</h5>
        </div>

      {/* KPI mini-cards with colors */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="shadow-sm border-0 bg-gradient w-100" style={{ background: "#EEF2FF" }}>
            <Card.Body className="py-3">
              <div className="d-flex align-items-center justify-content-between text-indigo-600">
                <span className="small fw-semibold">Total Revenue</span>
                <IndianRupee size={20} className="text-indigo-600" />
              </div>
              <div className="fw-bold text-indigo-800 mt-1" style={{ fontSize: 18 }}>
                {currency(totals.revenue)}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 w-100" style={{ background: "#ECFDF5" }}>
            <Card.Body className="py-3">
              <div className="d-flex align-items-center justify-content-between text-emerald-600">
                <span className="small fw-semibold">Total Orders</span>
                <ShoppingCart size={20} className="text-emerald-600" />
              </div>
              <div className="fw-bold text-emerald-800 mt-1" style={{ fontSize: 18 }}>
                {totals.orders}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-0 w-100" style={{ background: "#FFF7ED" }}>
            <Card.Body className="py-3">
              <div className="d-flex align-items-center justify-content-between text-amber-600">
                <span className="small fw-semibold">Top Product</span>
                <Crown size={20} className="text-amber-600" />
              </div>
              <div className="fw-bold text-amber-800 mt-1 text-truncate" style={{ fontSize: 16 }}>
                {totals.top?.productName || "-"}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="g-3">
        <Col md={6}>
          <Card className="shadow-sm border-0 h-100 w-100">
            <Card.Header className="bg-primary bg-opacity-10 py-2">
              <div className="d-flex align-items-center gap-2 text-primary fw-semibold small">
                <BarChart3 size={16} />
                Orders by Product
              </div>
            </Card.Header>
            <Card.Body className="py-2" style={{ height: 260 }}>
              {loading ? (
                <div className="d-flex h-100 align-items-center justify-content-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : noData ? (
                <div className="text-center text-muted small">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topOrdersAnalytics}>
                    <XAxis dataKey="productName" tick={false} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="orderCount">
                      <LabelList dataKey="orderCount" position="top" className="small" />
                      {topOrdersAnalytics.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm border-0 h-100 w-100">
            <Card.Header className="bg-success bg-opacity-10 py-2">
              <div className="d-flex align-items-center gap-2 text-success fw-semibold small">
                <PieChartIcon size={16} />
                Revenue Share
              </div>
            </Card.Header>
            <Card.Body className="py-2" style={{ height: 260 }}>
              {loading ? (
                <div className="d-flex h-100 align-items-center justify-content-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : noData ? (
                <div className="text-center text-muted small">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topOrdersAnalytics}
                      dataKey="totalPrice"
                      nameKey="productName"
                      innerRadius={50}
                      outerRadius={85}
                    >
                      {topOrdersAnalytics.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, _, { payload }) => [currency(value), payload.productName]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card className="shadow-sm border-0 mt-3 w-100">
        <Card.Header className="bg-dark bg-opacity-10 py-2 d-flex align-items-center justify-content-between">
          <span className="small fw-semibold text-dark">Detailed Report</span>
          <Button variant="outline-dark" size="sm" onClick={exportExcel} className="d-flex align-items-center gap-2">
            <Download size={16} /> Export
          </Button>
        </Card.Header>
        <Card.Body className="py-2">
          <Table responsive hover size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Image</th>
                <th>Product</th>
                <th className="text-end">Orders</th>
                <th className="text-end">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? skeletonRows.map((r) => (
                    <tr key={r.productId}>
                      <td>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f1f1f1" }} />
                      </td>
                      <td colSpan={3}>
                        <div className="placeholder-wave">
                          <span className="placeholder col-6"></span>
                        </div>
                      </td>
                    </tr>
                  ))
                : (topOrders.length ? topOrders : []).map((item) => (
                    <tr key={item.productId}>
                      <td>
                        <img
                          src={item.productImage}
                          alt={item.productName}
                          style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }}
                          loading="lazy"
                        />
                      </td>
                      <td>{item.productName}</td>
                      <td className="text-end">{item.orderCount}</td>
                      <td className="text-end">{currency(item.totalPrice)}</td>
                    </tr>
                  ))}
              {!loading && !topOrders.length && (
                <tr>
                  <td colSpan={4} className="text-center text-muted small">
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TopBestSellingReport;